import { Base } from '../../abstract/base.model';
import { isRecord } from '../utils/is-record';
import { idString, nullableBoolean, nullableNumber, nullableString } from '../utils/wire-value-utils';

/**
 * A member of an organization, as `GET /organizations/:id/memberships` returns it
 * (`user-organization-memberships-controller.ts`). Mirrors hub `data-models/organization-membership.ts`.
 *
 * The list sends the membership id as `membershipId`; it becomes `id` here. `role` holds the membership
 * roles (Pilot, Pilot in Command); `combinedRole` adds the org-level Owner and Chief Supervisor flags.
 * Both are `MembershipRoles` bit flags. `deletedAt` (from `Base`) is set for an inactive member.
 *
 * Read-modify-write rule: `role` (the only writable field) stays `undefined` when the hub didn't send it.
 */
export class OrganizationMembership extends Base {
  // Writable fields (PUT /organizations/:id/memberships/:profileId)
  role?: number | null;

  // Read-only fields
  organizationId?: string;
  profileId: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  combinedRole?: number;
  isOwner?: boolean;
  isChiefSupervisor?: boolean;
  isActive?: boolean;

  constructor(data: unknown) {
    super(data);
    const source = isRecord(data) ? data : {};
    this.id = idString(source['membershipId'] ?? source['id']);
    this.role = nullableNumber(source['role']);
    this.organizationId = nullableString(source['organizationId']) ?? undefined;
    this.profileId = idString(source['profileId']);
    this.email = nullableString(source['email']) ?? undefined;
    this.firstName = nullableString(source['firstName']) ?? undefined;
    this.lastName = nullableString(source['lastName']) ?? undefined;
    this.combinedRole = nullableNumber(source['combinedRole']) ?? undefined;
    this.isOwner = nullableBoolean(source['isOwner']) ?? undefined;
    this.isChiefSupervisor = nullableBoolean(source['isChiefSupervisor']) ?? undefined;
    this.isActive = nullableBoolean(source['isActive']) ?? undefined;
  }
}
